import { useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCanvasStore, useUIStore } from '@/stores';
import { UNSEGMENTED } from '@/stores/ui-store';
import type { NodeMetadata } from '@/services/canvas-mappers';

// Multi-select dropdown that filters the canvas by segment. v1 always groups
// by `role` (the segmentField in the store) — the group-by picker that lets
// the PM swap to team / company / focus is deliberately deferred until we
// have real segmented data to inform what the picker should look like.
export function SegmentFilter() {
  const { nodes } = useCanvasStore();
  const segmentField = useUIStore((s) => s.segmentField);
  const segmentFilter = useUIStore((s) => s.segmentFilter);
  const setSegmentFilter = useUIStore((s) => s.setSegmentFilter);

  // Distinct values of segment[segmentField] across the project's nodes,
  // alphabetised. UNSEGMENTED is appended at the end so it always sits below
  // real segments and reads as "everything else" rather than as a peer value.
  const { values, hasUnsegmented } = useMemo(() => {
    const real = new Set<string>();
    let unsegmented = false;
    for (const node of nodes) {
      const meta = node.data?.metadata as NodeMetadata | undefined;
      const value = meta?.segment?.[segmentField];
      if (value && value.length > 0) real.add(value);
      else unsegmented = true;
    }
    return {
      values: Array.from(real).sort((a, b) => a.localeCompare(b)),
      hasUnsegmented: unsegmented,
    };
  }, [nodes, segmentField]);

  const allOptions = useMemo(
    () => (hasUnsegmented ? [...values, UNSEGMENTED] : values),
    [values, hasUnsegmented],
  );

  // Always-enabled trigger. When there's nothing useful to filter by we still
  // open the popover and render an empty-state row inside — that's both more
  // discoverable than a tooltip on a disabled button (which most browsers
  // suppress) and more accessible (a `disabled` button can't be focused, so
  // keyboard users can't read the explanation at all).
  const isEmpty = values.length === 0 && !hasUnsegmented;
  const selectedCount = segmentFilter.length;

  const toggle = (value: string) => {
    if (segmentFilter.includes(value)) {
      setSegmentFilter(segmentFilter.filter((v) => v !== value));
    } else {
      setSegmentFilter([...segmentFilter, value]);
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          size="sm"
          variant={selectedCount > 0 ? 'default' : 'outline'}
          className="h-7 text-xs"
          aria-label="Filter by respondent segment"
        >
          <Filter className="h-3 w-3 mr-1" />
          Segment{selectedCount > 0 && ` (${selectedCount})`}
          <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-56 bg-background border rounded-md shadow-md p-1 text-xs"
        >
          <div className="px-2 py-1.5 flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Filter by {segmentField}
            </span>
            {selectedCount > 0 && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                onClick={() => setSegmentFilter([])}
              >
                <X className="h-2.5 w-2.5" />
                Clear
              </button>
            )}
          </div>
          {isEmpty ? (
            <div className="px-2 py-2 text-[11px] text-muted-foreground italic">
              No segmented respondents yet — invite someone with a role to start filtering.
            </div>
          ) : (
            <div role="menu" aria-label={`Filter by ${segmentField}`} className="max-h-64 overflow-y-auto py-0.5">
              {allOptions.map((value) => {
                const selected = segmentFilter.includes(value);
                const label = value === UNSEGMENTED ? 'Unsegmented' : value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={selected}
                    onClick={() => toggle(value)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-left"
                  >
                    <span
                      aria-hidden
                      className={`h-3.5 w-3.5 rounded border flex items-center justify-center shrink-0 ${
                        selected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                      }`}
                    >
                      {selected && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span
                      className={`truncate flex-1 ${value === UNSEGMENTED ? 'italic text-muted-foreground' : ''}`}
                      title={label}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
